import { formatFullStreet } from "../../util/format";
import {
  mapCountry,
  mapDateDocumentToPageModel,
  mapDatePageModelToRequest,
  mapNationality,
  mapNationalityString,
} from "../shared/mappers";
import { defaults, emptyPerson } from "./defaults";
import { PersonType, SignatoryModel } from "./types";
import {
  CardApplicationDocument,
  NaturalPersonDocument,
  SignatoriesDto,
} from "../../api/commercial/generated-types";
import { Gender } from "../../types";

const mapPageModelToRequest = (data: SignatoryModel): SignatoriesDto => {
  const isSameAsCardholder = data.isSameAsCardholder === "yes";
  const mapPerson = (person: PersonType, isSame?: boolean) => ({
    firstName: person.firstName,
    lastName: person.lastName,
    gender: person.gender as Gender,
    address: {
      ...person.address,
      country: mapCountry(person.address ? person.address.country : null),
    },
    birthdate: mapDatePageModelToRequest(person.birthdate),
    nationality: mapNationalityString(person.nationality),
    isSameAsCardholder: isSame,
  });

  const signatories: PersonType[] = isSameAsCardholder
    ? data.cardholderSignatories
    : data.otherSignatories;

  return {
    signatories: signatories.map((s, index) =>
      mapPerson(s, index === 0 && isSameAsCardholder),
    ),
  };
};

const mapDocumentToPageModel = (
  document: CardApplicationDocument,
): SignatoryModel => {
  const mapPerson = (person: NaturalPersonDocument) => ({
    firstName: person.firstName,
    lastName: person.lastName,
    gender: person.gender,
    fullStreet: formatFullStreet(person.address),
    address: { ...person.address, country: mapCountry(person.address.country) },
    birthdate: mapDateDocumentToPageModel(person.birthdate),
    nationality: mapNationality(person.nationality),
  });

  const signatories: NaturalPersonDocument[] = document.signatories;
  if (!signatories || signatories.length === 0) {
    return defaults(mapPerson(document.cardholder));
  }

  let secondSignatoryIndex = 1;
  if (document.cardholderIsSignatory) {
    secondSignatoryIndex = 0;
  }
  const hasSecondSignatory = signatories.length === secondSignatoryIndex + 1;

  const cardholderSignatories =
    hasSecondSignatory && document.cardholderIsSignatory
      ? [
          mapPerson(document.cardholder),
          mapPerson(signatories[secondSignatoryIndex]),
        ]
      : [mapPerson(document.cardholder)];

  const otherSignatories = document.cardholderIsSignatory
    ? [emptyPerson]
    : signatories.map((s) => mapPerson(s));

  return {
    isSameAsCardholder: document.cardholderIsSignatory ? "yes" : "no",
    cardholderSignatories,
    otherSignatories,
  };
};

const SigningMapper = {
  mapPageModelToRequest,
  mapDocumentToPageModel,
};

export default SigningMapper;
